import { observer } from 'mobx-react';
import { FC, ReactElement, useEffect, useMemo } from 'react';
import moment from 'moment';
import { getProductErrorMessage } from '../../../../../helpers/checkout';
import MenuProduct from '../../components/MenuProduct/MenuProduct';
import { localeStore } from 'src/mobx/localesStore';
import { menuStore } from 'src/mobx/menuStore';
import { modalStore } from 'src/mobx/modalStore';
import { IMenuProduct } from 'src/types/products';
import { emptyArray } from 'src/utils/helpers';
import { useCheckoutStoreContext } from '~/view/screens/CheckoutScreen/context';
import { DELIVERY_TYPE } from '~/constants/deliveryTypes';
import './style.scss';

const MenuProducts: FC = observer((): ReactElement => {
  const { store: checkoutStore } = useCheckoutStoreContext();

  useEffect(() => {
    menuStore.fetchMenuParts(menuStore.category, checkoutStore.dependencies.branch.id);
  }, []);

  const onSelectProduct = (product: IMenuProduct) => {
    modalStore.openOrderProduct({
      id: product.id,
      product,
      branch: checkoutStore.dependencies.branch,
      onSubmit: checkoutStore.addProduct,
      error: getProductErrorMessage(product, checkoutStore.supportedProductOrderType),
      isEdit: false,
      isDraft: checkoutStore.isDraft,
      time: checkoutStore.params.preorder && checkoutStore.params.preorder_date ? moment(checkoutStore.params.preorder_date).unix() : undefined,
      orderType: checkoutStore.params.is_dinein ? DELIVERY_TYPE.DINE_IN : (checkoutStore.params.delivery_type as DELIVERY_TYPE),
    });
  };

  const renderProduct = (product: IMenuProduct | undefined, index: number): ReactElement => {
    return (
      <MenuProduct key={product?.id || 'key-' + String(index)} product={product} index={index} onSelect={onSelectProduct} branch={menuStore.branch} />
    );
  };

  /** Filter all of the bottom products on the category page */
  const productsWithoutDonation = useMemo((): IMenuProduct[] => {
    return menuStore?.category?.items.data.filter((item) => !item.is_donation) || [];
  }, [menuStore?.category?.items.data]);

  /** Filter all donation products on the search page */
  const productsOnSearch = useMemo((): IMenuProduct[] => {
    return menuStore.categories.reduce((sum: IMenuProduct[], current) => sum.concat(current.items.data.filter((item) => !item.is_donation)), []);
  }, [menuStore?.categories]);

  if (menuStore.favorites && menuStore.favoriteProducts.length) {
    return <div className="menu-products">{menuStore.loading ? emptyArray().map(renderProduct) : menuStore.favoriteProducts.map(renderProduct)}</div>;
  }

  if ((!menuStore.favorites && menuStore.categories.length) || menuStore.loading) {
    if (menuStore.category) {
      return (
        <div className="menu-products">
          {productsWithoutDonation.map(renderProduct)}
          {menuStore.loading ? emptyArray().map(renderProduct) : null}
        </div>
      );
    }

    if (menuStore.parameters.search !== undefined || menuStore.isSearchActive) {
      return <div className="menu-products">{menuStore.loading ? emptyArray().map(renderProduct) : productsOnSearch.map(renderProduct)}</div>;
    }

    return <div className="menu-products">{emptyArray().map(renderProduct)}</div>;
  }

  return (
    <div className="menu-products flex-1 align-items-center justify-content-center">
      <p className="text-size-26 color-dark family-bold">{localeStore.t('no_products_found')}</p>
    </div>
  );
});

export default MenuProducts;
